import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <Button 
            onClick={() => navigate('/')}
            variant="ghost"
            className="mb-4 text-gray-700 hover:text-[#FFD000]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-3xl sm:text-4xl font-bold text-black">
            Yu<span className="text-[#FFD000]">Zu</span> Recruitment Services
          </h1>
          <p className="text-sm text-gray-600 mt-1">Zest for the right Talent</p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 sm:px-8 py-12">
        <h1 className="text-4xl font-bold text-black mb-8">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              YuZu Recruitment Services ("the Agency") values your privacy and is committed to protecting your personal information in compliance with the Protection of Personal Information Act (POPIA, Act No. 4 of 2013).
            </p>
            <p className="text-gray-700 leading-relaxed">
              This Privacy Policy outlines how we collect, use, disclose, and safeguard personal information of candidates, clients, and website visitors in connection with our recruitment and staffing services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">2. Personal Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may collect and process the following types of personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Identity details (full name, ID / passport number, nationality, date of birth)</li>
              <li>Contact details (email, phone number, address)</li>
              <li>Employment history, education, qualifications, and references</li>
              <li>Supporting documents such as CVs, letters of reference, and certificates</li>
              <li>Client data: company name, registration details, and billing information</li>
              <li>Website data such as cookies, IP addresses, and analytics usage</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">3. Purpose of Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              YuZu Recruitment Services collects and processes personal data to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Facilitate recruitment and placement processes</li>
              <li>Verify candidate qualifications and employment history</li>
              <li>Introduce candidates to potential employers</li>
              <li>Comply with employment and data protection legislation</li>
              <li>Manage internal communication, record-keeping, and invoicing</li>
              <li>Share job opportunities, training, or service updates (with consent)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">4. Lawful Basis for Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Processing is based on one or more of the following legal grounds:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Your explicit consent</li>
              <li>Contractual necessity</li>
              <li>Compliance with legal obligations</li>
              <li>Legitimate business interests including candidate sourcing, client servicing, placement facilitation, and workforce advisory activities conducted by YuZu Recruitment Services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">5. Data Sharing & Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may share personal information with:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Verified employers or clients directly involved in recruitment processes</li>
              <li>Background screening and reference verification agencies</li>
              <li>Third-party service providers (e.g., payroll, compliance, or IT support)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              YuZu Recruitment Services takes reasonable steps to ensure that all third-party service providers adhere to applicable data protection standards; however, YuZu cannot guarantee the ongoing compliance of independent third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">6. Data Retention & Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Personal data will be retained for up to 24 months from the date of last engagement, unless a longer retention period is required or permitted by law, or where necessary to resolve disputes, enforce agreements, or comply with regulatory obligations.
            </p>
            <p className="text-gray-700 leading-relaxed">
              YuZu Recruitment Services applies digital encryption, password-protected databases, and limited-access controls to protect your personal information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">7. Data Breach Notification</h2>
            <p className="text-gray-700 leading-relaxed">
              In the event of a data breach that compromises personal information, YuZu Recruitment Services will take reasonable steps to investigate, contain, and notify affected data subjects and the Information Regulator where required by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">8. Website Form Data</h2>
            <p className="text-gray-700 leading-relaxed">
              Personal information may also be collected through website forms, enquiry submissions, or direct communication initiated by the user.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              While YuZu Recruitment Services implements reasonable security measures, no method of electronic storage or transmission is completely secure. YuZu does not guarantee absolute security of personal information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">10. Your Rights Under POPIA</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Access your personal information held by YuZu Recruitment Services</li>
              <li>Request correction or deletion of inaccurate or outdated data</li>
              <li>Withdraw consent to processing at any time</li>
              <li>Lodge a complaint with the Information Regulator of South Africa if your data rights are violated</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise your rights, please contact <a href="mailto:info@yuzurecruit.co.za" className="text-[#FFD000] hover:underline">info@yuzurecruit.co.za</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">11. Cookies & Website Tracking</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies may include session cookies, analytics cookies, and functionality cookies. These are used to improve website performance and user experience. Users may manage cookie preferences via browser settings.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By continuing to use our website, you consent to our use of cookies under this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">12. Cross-Border Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              For international placements, personal data may be shared with employers outside South Africa. Such transfers will comply with POPIA and ensure adequate protection for your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">13. Policy Updates</h2>
            <p className="text-gray-700 leading-relaxed">
              YuZu Recruitment Services reserves the right to amend or update this Privacy Policy as needed. The latest version will always be available on our website, and continued engagement with our services constitutes acceptance of the latest version.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">14. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-2">
              <strong>Information Officer:</strong> Marché van Deventer / Ciara Wiese
            </p>
            <p className="text-gray-700 leading-relaxed mb-2">
              <strong>Email:</strong> <a href="mailto:info@yuzurecruit.co.za" className="text-[#FFD000] hover:underline">info@yuzurecruit.co.za</a>
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Website:</strong> <a href="http://www.yuzurecruit.co.za" className="text-[#FFD000] hover:underline" target="_blank" rel="noopener noreferrer">www.yuzurecruit.co.za</a>
            </p>
          </section>

          <div className="bg-gray-100 border-l-4 border-[#FFD000] p-6 mt-12">
            <p className="text-sm text-gray-600">
              <strong>Document Reference Code:</strong> YU/PRIV/2025-v1
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
